import defs from './_core/defs.js';
import triggerOne from './trigger/_triggerone.js';
import checkObjectType from './_helpers/checkobjecttype.js';
import is from './_helpers/is.js';
import forOwn from './_helpers/forown.js';
import assign from './_helpers/assign.js';
// a key named __proto__ is a key like any other: defined, so it never replaces the prototype
function write(object, key, value) {
  if (key === '__proto__') {
    Object.defineProperty(object, key, {
      value: value,
      writable: true,
      enumerable: true,
      configurable: true
    });
  } else {
    object[key] = value;
  }
}
export default function set(object, key, value, eventOptions) {
  if (typeof this === 'object' && this.isMatreshka) {
    // when context is Matreshka instance, use this as an object and shift other args
    eventOptions = value;
    value = key;
    key = object;
    object = this;
  } else {
    // throw error when object type is wrong
    checkObjectType(object, 'set');
  }
  // if no key or falsy key is given
  if (!key) {
    return object;
  }
  // allow to use key-value object as another method variation
  if (typeof key === 'object') {
    forOwn(key, function (objVal, objKey) {
      return set(object, objKey, objVal, value);
    });
    return object;
  }
  eventOptions = eventOptions || {};
  var def = defs.get(object);
  // if no object definition then make simple assignment
  if (!def) {
    write(object, key, value);
    return object;
  }
  var props = def.props,
    events = def.events;
  var propDef = props[key];
  // if no property definition then make simple assignment
  if (!propDef) {
    write(object, key, value);
    return object;
  }
  var previousValue = propDef.value,
    mediator = propDef.mediator;
  // possible flags, all of them are falsy by default
  var _eventOptions = eventOptions,
    skipMediator = _eventOptions.skipMediator,
    fromMediator = _eventOptions.fromMediator,
    force = _eventOptions.force,
    forceHTML = _eventOptions.forceHTML,
    silent = _eventOptions.silent,
    silentHTML = _eventOptions.silentHTML,
    skipCalc = _eventOptions.skipCalc;
  var newValue;
  if (mediator && !is(value, previousValue) && !skipMediator && !fromMediator) {
    // the mediators set by mediate wrap the user's one and read the rest themselves
    newValue = mediator(value);
  } else {
    newValue = value;
  }
  // an own setter runs first, and an own getter then gives the value the events and bindings see
  var accessor = propDef.accessor;
  if (accessor != null && accessor.set) {
    accessor.set.call(object, newValue);
    if (accessor.get) newValue = accessor.get.call(object);
  }
  var isChanged = !is(newValue, previousValue);
  // add to eventOptions object some useful properties
  // we override default eventOptions because some of the properties need to have actual values,
  // not inherited ones (eg when calc is used)
  var extendedEventOptions = assign({}, eventOptions, {
    value: newValue,
    self: object,
    previousValue: previousValue,
    key: key,
    isChanged: isChanged
  });
  var triggerChange = (isChanged || force) && !silent;
  // trigger beforechange:KEY and beforechange events
  if (triggerChange) {
    var beforechangeStr = 'beforechange';
    var beforechangeEventName = beforechangeStr + ":" + key;
    if (events[beforechangeEventName]) {
      triggerOne(object, beforechangeEventName, extendedEventOptions);
    }
    if (events[beforechangeStr]) {
      triggerOne(object, beforechangeStr, extendedEventOptions);
    }
  }
  propDef.value = newValue;
  // triger bindings
  if (!silentHTML && (isChanged || forceHTML)) {
    var changeBindingsEventName = "_change:bindings:" + key;
    if (events[changeBindingsEventName]) {
      triggerOne(object, changeBindingsEventName, extendedEventOptions);
    }
  }
  // trigger change:KEY and change events
  if (triggerChange) {
    var changeStr = 'change';
    var changeEventName = changeStr + ":" + key;
    if (events[changeEventName]) {
      triggerOne(object, changeEventName, extendedEventOptions);
    }
    // internal twin of "change" (used by Matreshka.Object), which off() cannot remove
    if (events._change) {
      triggerOne(object, '_change', extendedEventOptions);
    }
    if (events[changeStr]) {
      triggerOne(object, changeStr, extendedEventOptions);
    }
  }
  // trigger dependencies made by calc method
  if ((isChanged || force) && !skipCalc) {
    var changeDepsEventName = "_change:deps:" + key;
    if (events[changeDepsEventName]) {
      triggerOne(object, changeDepsEventName, extendedEventOptions);
    }
  }
  // a change handler may have set the key again: delegated listeners and deep bindings go to the value it holds now
  var branchEventOptions = is(propDef.value, newValue) ? extendedEventOptions : assign({}, extendedEventOptions, {
    value: propDef.value
  });
  // trigger common delegated events logic
  if (isChanged) {
    var changeDelegatedKeyEventName = "_change:delegated:" + key;
    if (events[changeDelegatedKeyEventName]) {
      triggerOne(object, changeDelegatedKeyEventName, branchEventOptions);
    }
  }
  // trigger tree change events logic: deep calc sources ("a.b.c") take force and skipCalc as plain ones do
  if (isChanged || force) {
    var changeTreeEventName = "_change:tree:" + key;
    if (events[changeTreeEventName]) {
      triggerOne(object, changeTreeEventName, branchEventOptions);
    }
  }
  if (isChanged) {
    // trigger other internal change events
    var changeCommonEventName = "_change:common:" + key;
    if (events[changeCommonEventName]) {
      triggerOne(object, changeCommonEventName, extendedEventOptions);
    }
    // trigger delegated logic for asterisk events (*.*.*@foo)
    // TODO: Confusing events names ("_change:delegated", "_change:common:KEY" etc)
    var changeDelegatedEventName = '_change:delegated';
    if (events[changeDelegatedEventName]) {
      triggerOne(object, changeDelegatedEventName, extendedEventOptions);
    }
  }
  return object;
}